import { createStore } from 'vuex';
import CryptoJS from 'crypto-js';

// 암호화 키 (반드시 안전한 곳에 보관되어야 함)
const ENCRYPTION_KEY = 'hamAppPassKey08811944773';

// 진단 하위 메뉴 인터페이스 정의
interface DiagnosisSubMenu {
  title: string;
  icon: string;
  to: string;
  id: number;
}

// 스낵바 인터페이스 정의
interface SnackbarState {
  show: boolean;
  text: string;
  color: string;
  timeout: number;
}

interface State {
  user: {
    id: string;
    name: string;
    role: number;
    role_name: string;
  } | null;
  drawer: boolean;
  mainDate: string;
  id: string | null;
  pw: string | null;
  role: number;
  roleName: string | null; // UI에서는 roleName으로 사용
  sisulName: string | null;
  empNum: string | null;
  code: string | null;
  name: string | null;
  isAutoLogin: boolean;
  NonStopId: string | null;
  NonStopPw: string | null;
  YYMM: string;
  diagnosisSubMenus: DiagnosisSubMenu[];
  snackbar: SnackbarState;
}

const encrypt = (data: State) => {
  const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
  return encryptedData;
};

const decrypt = (encryptedData: string) => {
  const decryptedData = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY).toString(CryptoJS.enc.Utf8);
  return JSON.parse(decryptedData);
};

const saveState = (state: State, isAutoLogin: boolean) => {
  if (isAutoLogin) {
    // 자동 로그인일 경우 localStorage에만 저장
    localStorage.setItem('vuex_state', encrypt(state));
    sessionStorage.removeItem('vuex_state'); // sessionStorage에서 제거
  } else {
    // 자동 로그인이 아닐 경우 sessionStorage에만 저장
    sessionStorage.setItem('vuex_state', encrypt(state));
    localStorage.removeItem('vuex_state'); // localStorage에서 제거
  }
};

const store = createStore<State>({
  state: {
    drawer: false,
    mainDate: '',
    id: null,
    pw: null,
    role: 0,
    roleName: null,
    sisulName: null,
    empNum: null,
    code: null,
    name: null,
    isAutoLogin: false, // 자동 로그인 여부
    NonStopId: null,
    NonStopPw: null,
    YYMM: '',
    user: null,
    diagnosisSubMenus: [],
    snackbar: {
      show: false,
      text: '',
      color: 'info',
      timeout: 4000
    }
  },
  mutations: {
    setId(state, id) {
      state.id = id;
      saveState(state, state.isAutoLogin);
    },
    setRole(state, role) {
      state.role = role;
      saveState(state, state.isAutoLogin);
    },
    setRoleName(state, roleName) {
      state.roleName = roleName;
      saveState(state, state.isAutoLogin);
    },
    setSisulName(state, sisulName) {
      state.sisulName = sisulName;
      saveState(state, state.isAutoLogin);
    },
    setEmpNum(state, empNum) {
      state.empNum = empNum;
      saveState(state, state.isAutoLogin);
    },
    
    setCode(state, code) {
      state.code = code;
      saveState(state, state.isAutoLogin);
    },
    setName(state, name) {
      state.name = name;
      saveState(state, state.isAutoLogin);
    },
    setDrawer(state, value) {
      state.drawer = value;
    },
    setAutoLogin(state, value) {
      state.isAutoLogin = value;
      saveState(state, value);
    },
    clearState(state) {
      state.id = null;
      state.role = 0;
      state.roleName = null;
      state.sisulName = null;
      state.empNum = null;
      state.code = null;
      state.name = null;
      state.isAutoLogin = false;
      state.diagnosisSubMenus = [];
      sessionStorage.removeItem('vuex_state');
      localStorage.removeItem('vuex_state');
    },
    setNonStopId(state, NonStopId) {
      state.NonStopId = NonStopId;
      sessionStorage.setItem('getNonStopId', encrypt(state));
    },
    setNonStopPw(state, NonStopPw) {
      state.NonStopPw = NonStopPw;
      sessionStorage.setItem('getNonStopPw', encrypt(state));
    },
    setYYMM(state, YYMM) {
      state.YYMM = YYMM;
      sessionStorage.setItem('getYYMM', encrypt(state));
    },
    addDiagnosisSubMenu(state, subMenu: DiagnosisSubMenu) {
      if (!state.diagnosisSubMenus) {
        state.diagnosisSubMenus = [];
      }
      state.diagnosisSubMenus.push(subMenu);
      saveState(state, state.isAutoLogin);
    },
    removeDiagnosisSubMenu(state, id: number) {
      if (!state.diagnosisSubMenus) {
        state.diagnosisSubMenus = [];
        return;
      }
      state.diagnosisSubMenus = state.diagnosisSubMenus.filter(menu => menu.id !== id);
      saveState(state, state.isAutoLogin);
    },
    clearDiagnosisSubMenus(state) {
      state.diagnosisSubMenus = [];
      saveState(state, state.isAutoLogin);
    },
    updateDiagnosisSubMenus(state, subMenus: DiagnosisSubMenu[]) {
      state.diagnosisSubMenus = subMenus;
      saveState(state, state.isAutoLogin);
    },
    // 스낵바 관련 뮤테이션
    showSnackbar(state, { text, color = 'info', timeout = 4000 }) {
      state.snackbar = {
        show: true,
        text,
        color,
        timeout
      };
    },
    hideSnackbar(state) {
      state.snackbar.show = false;
    }
  },
  actions: {
    // roleName 매개변수는 Member에서의 role_name 값을 수신
    login({ commit }, { id, role, roleName, sisulName, empNum, name, code, autoLogin }) {
      commit('setId', id);
      commit('setRole', role);
      commit('setRoleName', roleName); // role_name 값을 roleName으로 저장
      commit('setSisulName', sisulName);
      commit('setEmpNum', empNum);
      commit('setName', name);
      commit('setCode', code);
      commit('setAutoLogin', autoLogin); // 자동 로그인 설정
    },
    toggleDrawer({ commit, state }) {
      commit('setDrawer', !state.drawer);
    },
    logout({ commit }) {
      commit('clearState'); // 상태 초기화 및 스토리지 비우기
    },
    addDiagnosisDetail({ commit, state }, { fwaCode, compName }) {
      // 이미 같은 ID를 가진 서브메뉴가 있는지 확인
      const existingMenu = state.diagnosisSubMenus && 
        state.diagnosisSubMenus.find(menu => menu.id === fwaCode);
      
      // 이미 존재하지 않는 경우에만 새로 추가
      if (!existingMenu) {
        const subMenu = {
          title: `${compName} 진단`,
          icon: 'mdi-arrow-right-bottom',
          to: `/diagnosis/${fwaCode}`,
          id: fwaCode
        };
        commit('addDiagnosisSubMenu', subMenu);
      }
    },
    removeDiagnosisDetail({ commit }, fwaCode) {
      commit('removeDiagnosisSubMenu', fwaCode);
    }
  },
  getters: {
    isLoggedIn: (state) => !!state.role,
    id: (state) => state.id,
    pw: (state) => state.pw,
    role: (state) => state.role,
    roleName: (state) => state.roleName,
    sisulName: (state) => state.sisulName,
    empNum: (state) => state.empNum,
    name: (state) => state.name,
    code: (state) => state.code,
    getDrawerState: (state) => state.drawer,
    // 새로운 역할 확인 getter 추가
    isSuperVisor: (state) => state.role === 9,
    isTopManager: (state) => state.role === 8,
    isManager: (state) => state.role === 7,
    diagnosisSubMenus: (state) => state.diagnosisSubMenus || [],
    // 스낵바 관련 getter
    snackbar: (state) => state.snackbar
  },
});

// 페이지 로딩 시 저장된 암호화된 상태를 복호화하여 불러옵니다.
const savedState = localStorage.getItem('vuex_state') || sessionStorage.getItem('vuex_state');
if (savedState) {
  try {
    const decryptedState = decrypt(savedState);
    
    // 진단 서브메뉴 배열이 없으면 빈 배열로 초기화
    if (!decryptedState.diagnosisSubMenus) {
      decryptedState.diagnosisSubMenus = [];
    }
    
    store.replaceState(decryptedState);
  } catch (error) {
    console.error('저장된 상태를 복호화하는 중 오류가 발생했습니다:', error);
    localStorage.removeItem('vuex_state');
    sessionStorage.removeItem('vuex_state');
  }
}
const savedNonStopId = sessionStorage.getItem('getNonStopId');
if (savedNonStopId) {
  try {
    store.commit('setNonStopId', decrypt(savedNonStopId).NonStopId);
  } catch (error) {
    console.error('NonStopId를 복호화하는 중 오류가 발생했습니다:', error);
    sessionStorage.removeItem('getNonStopId');
  }
}
const savedNonStopPw = sessionStorage.getItem('getNonStopPw');
if (savedNonStopPw) {
  try {
    store.commit('setNonStopPw', decrypt(savedNonStopPw).NonStopPw);
  } catch (error) {
    console.error('NonStopPw를 복호화하는 중 오류가 발생했습니다:', error);
    sessionStorage.removeItem('getNonStopPw');
  }
}
const savedYYMM = sessionStorage.getItem('getYYMM');
if (savedYYMM) {
  try {
    store.commit('setYYMM', decrypt(savedYYMM).YYMM);
  } catch (error) {
    console.error('YYMM을 복호화하는 중 오류가 발생했습니다:', error);
    sessionStorage.removeItem('getYYMM');
  }
}
export default store;
